import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiDownload, FiEye, FiFileText, FiLayout, FiRotateCcw, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import api from '../config/api';
import IconButton from './IconButton';
import { appAlert } from '../utils/appDialog';
import './ResumePreview.css';

const FALLBACK_TEMPLATE = {
  id: 'builtin-classic',
  name: 'Classic',
  layout_key: 'classic',
  is_default: true,
  config: { accent: '#1e3a5f', font_size: 10, margin: 48, show_skills_inline: true },
};

function asTemplate(row) {
  if (!row) return FALLBACK_TEMPLATE;
  return {
    ...FALLBACK_TEMPLATE,
    ...row,
    layout_key: row.layout_key || 'classic',
    config: { ...FALLBACK_TEMPLATE.config, ...(row.config || {}) },
  };
}

function pickTemplate(templates, selectedId) {
  const list = (templates || []).map(asTemplate);
  if (selectedId) {
    const chosen = list.find((item) => String(item.id) === String(selectedId));
    if (chosen) return chosen;
  }
  return list.find((item) => item.is_default) || list[0] || FALLBACK_TEMPLATE;
}

function hasText(value) {
  return Boolean(String(value || '').trim());
}

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 1.6;
const ZOOM_STEP = 0.1;

function clampZoom(value) {
  const stepped = Math.round(value * 10) / 10;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(stepped.toFixed(1))));
}

function ResumePreview({ structured, fileName, templates = [], resumeId }) {
  const [selectedId, setSelectedId] = useState('');
  const [downloading, setDownloading] = useState('');
  const [zoom, setZoom] = useState(1);
  const frameRef = useRef(null);
  const paperRef = useRef(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const [paperHeight, setPaperHeight] = useState(0);
  const content = structured || {};
  const labels = content.section_labels || {};
  const contact = content.contact || {};
  const template = useMemo(
    () => pickTemplate(templates, selectedId),
    [templates, selectedId]
  );
  const layout = template.layout_key === 'modern' || template.layout_key === 'compact'
    ? template.layout_key
    : 'classic';
  const accent = template.config.accent || '#1e3a5f';
  const fontSize = Math.max(12, Number(template.config.font_size) || 10) + 2;
  const contactBits = [contact.email, contact.phone, contact.location, contact.linkedin].filter(hasText);
  const skills = (content.skills || []).map((skill) => String(skill || '').trim()).filter(Boolean);
  const name = contact.name || fileName || 'Your name';
  const experience = (content.experience || []).filter((item) => hasText(item.title) || hasText(item.company) || hasText(item.location) || (item.bullets || []).some(hasText));
  const education = (content.education || []).filter((item) => hasText(item.degree) || hasText(item.school));
  const certifications = (content.certifications || []).filter((item) => hasText(item.name));
  const languages = (content.languages || []).filter((item) => hasText(item.language));
  const projects = (content.projects || []).filter((item) => hasText(item.name) || hasText(item.description));
  const customSections = (content.custom_sections || []).filter((item) => hasText(item.body) || hasText(item.label));
  const hasBody = hasText(content.summary) || experience.length || education.length || skills.length || certifications.length || languages.length || projects.length || customSections.length;

  const downloadResume = async (format) => {
    if (!resumeId || downloading) return;
    setDownloading(format);
    try {
      const res = await api.post(
        `/resumes/${resumeId}/export`,
        {
          format,
          template_id: selectedId || undefined,
          structured_content: content,
          file_name: fileName,
        },
        { responseType: 'blob' }
      );
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const ext = format === 'docx' ? 'docx' : 'pdf';
      const base = String(fileName || 'resume').replace(/\.(pdf|docx?)$/i, '').trim() || 'resume';
      const filename = match?.[1] || `${base}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = 'Could not download the resume.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed.error) message = parsed.error;
        } catch {
          /* keep the fallback message */
        }
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }
      void appAlert(message);
    } finally {
      setDownloading('');
    }
  };

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const paper = paperRef.current;
    if (!frame || !paper) return undefined;
    const measure = () => {
      const nextWidth = Math.round(frame.clientWidth);
      const nextHeight = Math.round(paper.offsetHeight);
      setFrameWidth((current) => (current === nextWidth ? current : nextWidth));
      setPaperHeight((current) => (current === nextHeight ? current : nextHeight));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(paper);
    return () => observer.disconnect();
  }, []);

  const measured = frameWidth > 0 && paperHeight > 0;
  let stageStyle;
  let scaleStyle;
  if (measured && zoom > 1) {
    stageStyle = {
      width: `${Math.round(frameWidth * zoom)}px`,
      height: `${Math.round(paperHeight * zoom) + 16}px`,
      alignSelf: 'flex-start',
      flexShrink: 0,
    };
    scaleStyle = {
      position: 'absolute',
      top: 0,
      left: `${Math.round((frameWidth * (zoom - 1)) / 2)}px`,
      width: `${frameWidth}px`,
      transform: `scale(${zoom})`,
      transformOrigin: 'top center',
    };
  } else if (measured && zoom < 1) {
    stageStyle = {
      height: `${Math.round(paperHeight * zoom) + 16}px`,
      overflow: 'hidden',
    };
    scaleStyle = {
      transform: `scale(${zoom})`,
      transformOrigin: 'top center',
    };
  }

  return (
    <div className="resume-preview" ref={frameRef}>
      <div className="resume-preview-toolbar">
        <div className="studio-pane-title">
          <FiEye /> Preview
        </div>
        <div className="resume-preview-actions">
          <label className="resume-preview-select">
            <FiLayout aria-hidden />
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              aria-label="Resume template"
            >
              <option value="">
                Default{template && !selectedId ? ` · ${template.name}` : ''}
              </option>
              {(templates || []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}{item.is_default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="resume-preview-zoom" role="group" aria-label="Preview zoom">
            <IconButton
              icon={FiZoomOut}
              label="Zoom out"
              size="sm"
              disabled={zoom <= ZOOM_MIN}
              onClick={() => setZoom((current) => clampZoom(current - ZOOM_STEP))}
            />
            <IconButton
              icon={FiZoomIn}
              label="Zoom in"
              size="sm"
              disabled={zoom >= ZOOM_MAX}
              onClick={() => setZoom((current) => clampZoom(current + ZOOM_STEP))}
            />
            <IconButton
              icon={FiRotateCcw}
              label="Reset zoom"
              size="sm"
              disabled={zoom === 1}
              onClick={() => setZoom(1)}
            />
          </div>
          <IconButton
            icon={FiDownload}
            label="Download PDF"
            size="sm"
            variant="primary"
            loading={downloading === 'pdf'}
            disabled={!resumeId || Boolean(downloading)}
            onClick={() => downloadResume('pdf')}
          />
          <IconButton
            icon={FiFileText}
            label="Download Word"
            size="sm"
            loading={downloading === 'docx'}
            disabled={!resumeId || Boolean(downloading)}
            onClick={() => downloadResume('docx')}
          />
        </div>
      </div>
      {template.description ? <p className="resume-preview-note">{template.description}</p> : null}
      <div className="resume-preview-stage" style={stageStyle}>
        <div className="resume-preview-scale" style={scaleStyle}>
      <article
        ref={paperRef}
        className={`resume-preview-paper resume-preview-paper--${layout}`}
        style={{
          '--preview-accent': accent,
          '--preview-size': `${fontSize}px`,
          '--preview-pad': `${Math.max(18, Math.round((Number(template.config.margin) || 48) / 2))}px`,
        }}
        aria-label="Resume preview"
      >
        <header className="resume-preview-header">
          <h2>{name}</h2>
          {contactBits.length ? <p>{contactBits.join('  ·  ')}</p> : null}
        </header>

        {hasText(content.summary) && (
          <section>
            <h3>{labels.summary || 'Professional summary'}</h3>
            <p>{content.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h3>{labels.experience || 'Experience'}</h3>
            {experience.map((item) => (
              <div key={item.id} className="resume-preview-role">
                <div className="resume-preview-role-head">
                  <strong>{[item.title, item.company, item.location].filter(hasText).join(' — ') || 'Role'}</strong>
                  {hasText(item.dates) ? <span>{item.dates}</span> : null}
                </div>
                <ul>
                  {(item.bullets || []).filter(hasText).map((bullet, index) => (
                    <li key={`${item.id}-${index}`}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h3>{labels.education || 'Education'}</h3>
            {education.map((item) => (
              <div key={item.id} className="resume-preview-role">
                <div className="resume-preview-role-head">
                  <strong>{[item.degree, item.school].filter(hasText).join(' — ')}</strong>
                  {hasText(item.dates) ? <span>{item.dates}</span> : null}
                </div>
              </div>
            ))}
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h3>{labels.skills || 'Skills'}</h3>
            {template.config.show_skills_inline === false ? (
              <ul className="resume-preview-skills">
                {skills.map((skill) => <li key={skill}>{skill}</li>)}
              </ul>
            ) : (
              <p>{skills.join(' · ')}</p>
            )}
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h3>{labels.certifications || 'Certifications'}</h3>
            {certifications.map((item) => (
              <p key={item.id}>{[item.name, item.issuer, item.date].filter(hasText).join(' — ')}</p>
            ))}
          </section>
        )}

        {languages.length > 0 && (
          <section>
            <h3>{labels.languages || 'Languages'}</h3>
            <p>
              {languages
                .map((item) => (item.proficiency ? `${item.language} (${item.proficiency})` : item.language))
                .join(' · ')}
            </p>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h3>{labels.projects || 'Projects'}</h3>
            {projects.map((item) => (
              <div key={item.id} className="resume-preview-role">
                <strong>{item.name || 'Project'}</strong>
                {hasText(item.description) ? <p>{item.description}</p> : null}
              </div>
            ))}
          </section>
        )}

        {customSections.map((item) => (
          <section key={item.id}>
            <h3>{item.label || 'Section'}</h3>
            {hasText(item.body) ? <p className="resume-preview-pre">{item.body}</p> : null}
          </section>
        ))}

        {!hasBody && (
          <p className="resume-preview-empty">Sections you fill in appear here as you type.</p>
        )}
      </article>
        </div>
      </div>
    </div>
  );
}

export default ResumePreview;
